import { useState } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { CheckCircle, XCircle, Clock, AlertTriangle, User, Bot, ThumbsUp, ThumbsDown, Edit3 } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";

export const StatCard = ({ icon: Icon, label, value, subtext, color }) => (
  <Card className="border-slate-200">
    <CardContent className="p-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-900">{value}</p>
          <p className="text-sm text-slate-500">{label}</p>
          {subtext && <p className="text-xs text-slate-400">{subtext}</p>}
        </div>
      </div>
    </CardContent>
  </Card>
);

const statusClass = (log) => {
  if (log.error) return "border-red-200 bg-red-50";
  if (log.response_sent) return "border-emerald-200 bg-emerald-50";
  return "border-slate-200 bg-white";
};

const StatusIcon = ({ log }) => {
  if (log.response_sent) return <CheckCircle className="w-4 h-4 text-emerald-500" />;
  if (log.error) return <XCircle className="w-4 h-4 text-red-500" />;
  return <Clock className="w-4 h-4 text-slate-400" />;
};

export const LogEntry = ({ log, onFeedback }) => {
  const [expanded, setExpanded] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState(null);
  
  const handleQuickFeedback = async (rating, e) => {
    e.stopPropagation();
    if (!log.message_text || !log.ai_response) return;
    
    try {
      await axios.post(`${API}/ai-training/feedback`, {
        webhook_log_id: log.log_id,
        original_question: log.message_text,
        original_ai_response: log.ai_response,
        rating: rating
      }, { withCredentials: true });
      
      setFeedbackGiven(rating);
      toast.success(rating === "positive" ? "Marked as good response!" : "Marked for correction");
      
      if (rating === "negative" && onFeedback) {
        onFeedback(log);
      }
    } catch (error) {
      toast.error("Failed to save feedback");
    }
  };
  
  return (
    <div 
      className={`border rounded-lg p-3 cursor-pointer transition-all ${statusClass(log)}`}
      onClick={() => setExpanded(!expanded)}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <StatusIcon log={log} />
          <span className="text-sm font-medium text-slate-700">
            {log.event_type || "conversation.newMessage"}
          </span>
          <Badge variant="outline" className={`text-xs ${log.sender_type === "HUMAN_AGENT" ? "border-sky-200 text-sky-700 bg-sky-50" : ""}`}>
            {log.sender_type || "UNKNOWN"}
          </Badge>
          {log.skipped_reason === "human_agent_active" && (
            <Badge variant="outline" className="text-xs border-sky-200 text-sky-700 bg-sky-50" data-testid="human-active-badge">
              HUMAN HANDLING
            </Badge>
          )}
          {log.auto_responder_disabled && (
            <Badge variant="outline" className="text-xs border-amber-200 text-amber-600 bg-amber-50">
              AUTO-OFF
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          {log.processing_time_ms && (
            <span>{log.processing_time_ms}ms</span>
          )}
          <span>{new Date(log.created_at).toLocaleTimeString()}</span>
        </div>
      </div>
      
      {expanded && (
        <div className="mt-3 pt-3 border-t border-slate-200 space-y-2">
          {log.message_text && (
            <div className="flex gap-2">
              <User className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-slate-500">Customer Message</p>
                <p className="text-sm text-slate-700">{log.message_text}</p>
              </div>
            </div>
          )}
          {log.ai_response && (
            <div className="flex gap-2">
              <Bot className="w-4 h-4 text-violet-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs font-medium text-slate-500">AI Response</p>
                <p className="text-sm text-slate-700">{log.ai_response}</p>
                
                {/* Quick Feedback Buttons */}
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs text-slate-400">Rate this response:</span>
                  <Button
                    size="sm"
                    variant={feedbackGiven === "positive" ? "default" : "outline"}
                    className={`h-7 px-2 gap-1 ${feedbackGiven === "positive" ? "bg-emerald-500 hover:bg-emerald-600" : ""}`}
                    onClick={(e) => handleQuickFeedback("positive", e)}
                    disabled={feedbackGiven !== null}
                  >
                    <ThumbsUp className="w-3 h-3" />
                    Good
                  </Button>
                  <Button
                    size="sm"
                    variant={feedbackGiven === "negative" ? "default" : "outline"}
                    className={`h-7 px-2 gap-1 ${feedbackGiven === "negative" ? "bg-red-500 hover:bg-red-600" : ""}`}
                    onClick={(e) => handleQuickFeedback("negative", e)}
                    disabled={feedbackGiven !== null}
                  >
                    <ThumbsDown className="w-3 h-3" />
                    Needs Fix
                  </Button>
                  {feedbackGiven === "negative" && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 gap-1 border-violet-200 text-violet-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        onFeedback && onFeedback(log);
                      }}
                    >
                      <Edit3 className="w-3 h-3" />
                      Add Correction
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
          {log.error && (
            <div className="flex gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-red-600">Error</p>
                <p className="text-sm text-red-700">{log.error}</p>
              </div>
            </div>
          )}
          <div className="text-xs text-slate-400">
            Thread ID: {log.thread_id || "N/A"} | Message ID: {log.message_id || "N/A"}
          </div>
        </div>
      )}
    </div>
  );
};
